// RAPIFAST :: componentes compartidos de las pantallas de verificación (demo).
import { ShieldAlert, X } from "lucide-react";
import { SEMAFORO } from "@/lib/verificationMock";

export const Badge = ({ tone, children, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold ${tone.cls}`}
  >
    {children}
  </span>
);

export const SemaforoBadge = ({ level, testId }) => {
  const s = SEMAFORO[level];
  return (
    <Badge tone={s} testId={testId}>
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.dot }} />
      {s.label}
    </Badge>
  );
};

export const DemoBanner = () => (
  <div
    data-testid="verification-demo-banner"
    className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-300"
  >
    <ShieldAlert size={18} className="mt-0.5 shrink-0" />
    <div>
      <span className="font-bold">Modo demostración.</span> Datos ficticios en memoria del
      navegador; sin conexión a la base de datos real, sin archivos reales y sin efectos sobre el
      piloto. Los cambios se pierden al recargar.
    </div>
  </div>
);

export const Field = ({ label, value, sensitive, testId }) => (
  <div data-testid={testId}>
    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
    <div className="mt-0.5 text-sm text-gray-200">
      {value || <span className="text-gray-600">No registrado</span>}
      {sensitive && value && (
        <span className="ml-2 rounded-full border border-[#333] bg-[#1F1F1F] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
          Dato sensible
        </span>
      )}
    </div>
  </div>
);

export const SectionCard = ({ title, children, testId }) => (
  <section
    data-testid={testId}
    className="rounded-2xl border border-[#27272A] bg-[#141416] p-4 shadow-lg sm:p-5"
  >
    <h3 className="font-heading mb-3 text-sm font-bold uppercase tracking-wider text-[#FF6A00]">
      {title}
    </h3>
    {children}
  </section>
);

// Visor de documento confidencial. En DEMO puede mostrar el archivo de prueba
// adjunto en el navegador (dataUrl). Con 0025 usará una URL firmada de 5 minutos.
export const ConfidentialDocModal = ({ docLabel, ownerName, file, onClose }) => {
  const hasReal = file && file.dataUrl;
  const isImg = hasReal && file.fileType?.startsWith("image/");
  const isPdf = hasReal && file.fileType === "application/pdf";
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="sensitive-doc-modal"
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-[#27272A] bg-[#141416] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          data-testid="sensitive-doc-watermark-banner"
          className="bg-red-600/90 px-4 py-2 text-center text-xs font-extrabold uppercase tracking-wider text-white"
        >
          Documento confidencial · Acceso restringido a administrador autorizado
        </div>
        <div className="relative p-6">
          <h4 className="font-heading text-lg font-bold text-white">{docLabel}</h4>
          <p className="mt-1 text-sm text-gray-400">{ownerName}</p>
          {file?.fileName && (
            <p className="mt-1 text-xs text-gray-500" data-testid="sensitive-doc-filename">
              Archivo de prueba: {file.fileName}
            </p>
          )}
          <div className="mt-5 overflow-hidden rounded-xl border border-dashed border-[#333] bg-[#101012]" data-testid="sensitive-doc-preview">
            {isImg && <img src={file.dataUrl} alt={docLabel} className="max-h-72 w-full object-contain" />}
            {isPdf && <iframe title={docLabel} src={file.dataUrl} className="h-72 w-full" />}
            {hasReal && !isImg && !isPdf && (
              <a href={file.dataUrl} download={file.fileName} className="block p-6 text-center text-sm text-[#FF6A00] underline">
                Descargar archivo de prueba
              </a>
            )}
            {!hasReal && (
              <div className="grid h-40 place-items-center p-4 text-center text-xs text-gray-500">
                {file?.tooLarge
                  ? "El archivo de prueba supera el límite del modo demo y no se guardó para vista previa."
                  : "Sin archivo adjunto. En producción se abrirá con una URL firmada temporal del almacenamiento privado (0025)."}
              </div>
            )}
          </div>
          <p className="mt-3 text-[11px] text-gray-600">
            Vista previa en el navegador (modo demo). No hay almacenamiento seguro conectado todavía.
          </p>
          <button
            onClick={onClose}
            data-testid="sensitive-doc-close-button"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-[#333] bg-[#1F1F1F] px-4 py-2.5 text-sm font-bold text-gray-300 hover:bg-[#27272A]"
          >
            <X size={16} /> Cerrar visor
          </button>
        </div>
      </div>
    </div>
  );
};
